import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Ticket, DollarSign, TrendingUp, Users, BarChart3, Calendar,
  CreditCard, Check, ArrowRight, Banknote,
} from "lucide-react";

import { toast } from "sonner";

export default function AdminTicketSalesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [autoPayoutLoading, setAutoPayoutLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, evRes, payRes] = await Promise.all([
        api.get("/admin/tickets/overview"),
        api.get("/admin/tickets/events"),
        api.get("/admin/tickets/payouts"),
      ]);
      setOverview(ovRes.data);
      setEvents(evRes.data.events || []);
      setPayouts(payRes.data.payouts || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAutoPayout = async () => {
    if (!window.confirm("Traiter automatiquement les paiements pour tous les evenements passes ?")) return;
    setAutoPayoutLoading(true);
    try {
      const res = await api.post("/admin/tickets/auto-payouts");
      toast.success(`${res.data.processed} paiement(s) traite(s)`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setAutoPayoutLoading(false);
  };

  const handleProcessPayout = async (eventId) => {
    if (!window.confirm("Declencher le paiement pour cet evenement ?")) return;
    try {
      await api.post(`/admin/tickets/payouts/${eventId}/process`);
      toast.success("Paiement traite !");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div className="py-6"><div className="bg-card rounded-3xl h-48 animate-pulse" /></div>;

  const stats = overview?.stats || {};

  return (
    <div className="space-y-6" data-testid="admin-tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-extrabold text-xl text-card-foreground flex items-center gap-2">
            <Ticket className="w-6 h-6 text-[#FF6B35]" />
            Ventes de billets
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Suivi global de la billetterie</p>
        </div>
        <button
          onClick={handleAutoPayout}
          disabled={autoPayoutLoading}
          className="flex items-center gap-1.5 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
          data-testid="auto-payout-btn"
        >
          <Banknote className="w-4 h-4" /> {autoPayoutLoading ? "..." : "Auto-paiements"}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Ticket, label: "Billets vendus", value: stats.total_tickets_sold || 0, color: "text-[#FF6B35]", bg: "bg-[#FF6B35]/10" },
          { icon: DollarSign, label: "CA Total", value: `${((stats.total_revenue || 0) / 100).toFixed(0)} EUR`, color: "text-emerald-500", bg: "bg-emerald-500/10" },
          { icon: CreditCard, label: "Commission ReallyFan", value: `${((stats.platform_fees || 0) / 100).toFixed(0)} EUR`, color: "text-[#7000FF]", bg: "bg-[#7000FF]/10" },
          { icon: TrendingUp, label: "Reverse artistes", value: `${((stats.artist_revenue || 0) / 100).toFixed(0)} EUR`, color: "text-amber-500", bg: "bg-amber-500/10" },
        ].map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2">
              <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <p className="font-heading font-extrabold text-xl text-card-foreground">{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-card rounded-full p-1 border border-border">
        {[
          { key: "overview", icon: BarChart3, label: "Evenements" },
          { key: "payouts", icon: Banknote, label: "Paiements" },
          { key: "recent", icon: Calendar, label: "Ventes recentes" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-medium transition-all ${
              tab === t.key ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-2">
          {events.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">Aucun evenement avec billetterie</p>
          ) : events.map((e) => (
            <div key={e.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`admin-event-${e.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-sm text-card-foreground truncate">{e.title}</p>
                  <p className="text-[10px] text-muted-foreground">{e.artist_name} - {new Date(e.date).toLocaleDateString("fr-FR")}</p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-bold text-card-foreground">{e.ticket_stats?.sold}/{e.ticket_stats?.capacity}</p>
                    <p className="text-[10px] text-muted-foreground">{((e.ticket_stats?.revenue || 0) / 100).toFixed(0)} EUR</p>
                  </div>
                  <button
                    onClick={() => handleProcessPayout(e.id)}
                    className="text-xs bg-emerald-500/10 text-emerald-500 font-bold px-3 py-1 rounded-full hover:bg-emerald-500/20 transition-colors"
                    data-testid={`payout-btn-${e.id}`}
                  >
                    Payer
                  </button>
                </div>
              </div>
              <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-[#FF6B35] rounded-full" style={{
                  width: `${e.ticket_stats?.capacity > 0 ? (e.ticket_stats.sold / e.ticket_stats.capacity * 100) : 0}%`
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "payouts" && (
        <div className="space-y-2">
          {payouts.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">Aucun paiement effectue</p>
          ) : payouts.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4" data-testid={`payout-${p.id}`}>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-card-foreground truncate">{p.event_title}</p>
                <p className="text-[10px] text-muted-foreground">
                  {p.artist_name} - {new Date(p.processed_at).toLocaleString("fr-FR")}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-emerald-500">{(p.artist_payout / 100).toFixed(2)} EUR</p>
                <p className="text-[10px] text-muted-foreground">Commission: {(p.platform_fees / 100).toFixed(2)} EUR</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "recent" && (
        <div className="space-y-2">
          {(overview?.recent_sales || []).length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">Aucune vente recente</p>
          ) : (overview?.recent_sales || []).map((s) => (
            <div key={s.id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#FF6B35]/10 flex items-center justify-center shrink-0">
                <Ticket className="w-4 h-4 text-[#FF6B35]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-card-foreground">{s.buyer_pseudo}</p>
                <p className="text-[10px] text-muted-foreground">
                  {s.quantity} billet{s.quantity > 1 ? "s" : ""} - {new Date(s.created_at).toLocaleString("fr-FR")}
                </p>
              </div>
              <p className="text-sm font-bold text-card-foreground shrink-0">{(s.total / 100).toFixed(2)} EUR</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
